import React from 'react';
import { kebabCase } from 'lodash-es';
import type { MDXComponents } from 'mdx/types';

import { styled } from '@linaria/react';

import { Heading } from '@/components/elements/heading';
import * as Icons from '@/components/elements/icon';
import { Image } from '@/components/elements/image';
import { Link } from '@/components/elements/link';
import { Text } from '@/components/elements/text';
import { Accordion } from '@/components/modules/accordion';
import { BlockCode, InlineCode } from '@/components/modules/block-code';
import { CTABox } from '@/components/modules/cta-box';
import { fontSizes, fontWeight } from '@/styles/tokens';

const StyledLink = styled(Link)`
  text-decoration: underline;
  color: var(--text-dark-grey);
`;

const StyledText = styled(Text)`
  margin-bottom: 1.25rem;
`;

const StyledHighlightedParagraphs = styled.div`
  p {
    margin-bottom: 2rem;
    color: var(--text-dark-grey);
    font-size: ${fontSizes.text.l};
    font-weight: ${fontWeight.bold};
    line-height: 1.5;
  }
`;

const StyledBlockquote = styled.blockquote`
  margin: 2rem 0;
  padding: 0 1.5rem;
  border-left: 4px solid var(--text-white);
  background-color: var(--background-light-grey);
  color: var(--text-dark-grey);
  font-size: ${fontSizes.text.m};
  line-height: 1.5;
  font-style: italic;
`;

const StyledHeading = styled.div`
  margin-bottom: 24px;
  margin-top: var(--heading-margin, 0px);
`;

const StyledUL = styled.ul`
  list-style: disc;
  padding-left: 30px;
  color: var(--text-dark-grey);
  font-size: ${fontSizes.text.m};

  ul, ol {
    margin-top: 2px;
    margin-bottom: 2px;
  }

  li {
    margin-bottom: 1.25rem;

    > ul, > ol {
      margin-top: 1.25rem;
    }
  }
`;

const StyledOL = styled.ol`
  list-style: decimal;
  padding-left: 30px;
  color: var(--text-dark-grey);
  font-size: ${fontSizes.text.m};

  ul, ol {
    margin-top: 2px;
    margin-bottom: 2px;
  }

  li {
    margin-bottom: 1.25rem;

    > ul, > ol {
      margin-top: 1.25rem;
    }
  }
`;

const StyledImage = styled(Image)`
  display: block;
  position: relative !important;
  max-width: 100%;
  margin: 48px auto;
`;

const Heading2 = ({ children }: Component) => {
  return (
    <StyledHeading style={{ '--heading-margin': '48px' } as React.CSSProperties}>
      <Heading as="h2" fontSize="m" color="lightGrey" id={kebabCase(children?.toString())}>
        {children}
      </Heading>
    </StyledHeading>
  );
};

const Heading3to6 = ({ children }: Component) => {
  return (
    <StyledHeading style={{ '--heading-margin': '24px' } as React.CSSProperties}>
      <Heading as="h3" fontSize="s" color="lightGrey" id={kebabCase(children?.toString())}>
        {children}
      </Heading>
    </StyledHeading>
  );
};

export const defaultComponents: MDXComponents = {
  h2: Heading2,
  h3: Heading3to6,
  h4: Heading3to6,
  h5: Heading3to6,
  h6: Heading3to6,
  a({ children, href }: React.ComponentProps<'a'>) {
    return <StyledLink href={href ?? ''}>{children}</StyledLink>;
  },
  p({ children }: Component) {
    return (
      <StyledText fontSize="m" color="darkGrey">
        {children}
      </StyledText>
    );
  },
  HighlightedParagraphs({ children }: Component) {
    return <StyledHighlightedParagraphs>{children}</StyledHighlightedParagraphs>;
  },
  Accordion,
  CTABox,
  ul({ children }: Component) {
    return <StyledUL>{children}</StyledUL>;
  },
  ol({ children }: Component) {
    return <StyledOL>{children}</StyledOL>;
  },
  // Triple-tick ``` code renders as <pre><code>... blocks, while inline code
  // renders as a single <code> element.
  pre: ({ children }: { children?: React.ReactNode }) => {
    const codeElement = children as React.ReactElement<{ children?: React.ReactNode; className?: string }>;
    const { children: content, className } = codeElement.props;

    return <pre>
      <BlockCode content={content} language={className} />
    </pre>;
  },
  code({ children }) {
    return <InlineCode>{children}</InlineCode>;
  },
  blockquote({ children }: Component) {
    return <StyledBlockquote>{children}</StyledBlockquote>;
  },
  ...Icons,
};

function setImagePath(path: string, parentFolder: string) {
  const imageSegments = path?.split('/');
  const imageFilename = imageSegments[imageSegments.length - 1];

  return `/images/${parentFolder}/${imageFilename}`;
}

const imageResolver = ({
  src,
  alt,
  title,
  imagePathPrefix,
}: {
  src?: string;
  alt?: string;
  title?: string;
  imagePathPrefix: string;
}) => {
  const realSRC = setImagePath(src || '', imagePathPrefix);

  return (
    <StyledImage wrapperAs="span" src={realSRC} alt={alt ?? ''} title={title} width={1024} height={768} />
  );
};

export const docsComponents: MDXComponents = {
  img: ({ src, alt, title }) => imageResolver({ src, title, alt, imagePathPrefix: 'docs' }),
};

export const postComponents: MDXComponents = {
  img: ({ src, alt, title }) => imageResolver({ src, title, alt, imagePathPrefix: 'posts' }),
};
